import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCLoader from '@/Atoms/CCLoader';
import {useGetPlayerClubsApi} from '@/Hooks/PlayerProfileApi/useGetPlayerClubsApi';
import ProfileAsset, {ProfileAssetName} from '../../ui/ProfileAsset';

const styles = StyleSheet.create({
  columns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  leftCol: {
    flex: 2,
    minWidth: 280,
  },
  rightCol: {
    flex: 1,
    minWidth: 220,
  },
  // Spec §2 card shell: radius 20, padding 24/26, soft shadow, no border.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 24,
    paddingHorizontal: 26,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  // 18px/600 dark title, matching Overview.
  sectionLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 12,
  },
  clubGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  clubCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FAF6EB',
    borderRadius: 14,
    padding: 12,
    minWidth: 180,
    flexGrow: 1,
  },
  clubAvatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#3DAB9E',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  clubAvatarText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#fff',
  },
  clubName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  clubRole: {
    fontSize: 11,
    color: '#888280',
    textTransform: 'capitalize',
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  rowTitle: {
    fontSize: 13,
    color: '#262322',
  },
  rowMeta: {
    fontSize: 11,
    color: '#888280',
  },
  medal: {
    marginRight: 8,
  },
  // Ordinal placement ("2nd") right-aligned, colored by tier (gold for a
  // podium finish).
  rankText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#4D3F37',
  },
  challengeAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#937ADB',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  challengeAvatarText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#fff',
  },
  challengeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F0EDE7',
  },
  stateChip: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'capitalize',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  emptyText: {
    fontSize: 12,
    color: '#888280',
  },
  moreText: {
    fontSize: 12,
    color: '#888280',
    marginTop: 10,
  },
});

// Cap the tournaments list; the title still shows the full count.
const TOURNAMENTS_LIMIT = 5;

const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

// Design shows tournament placement as an ordinal ("1st", "2nd", "11th"), not
// a "Rank N" chip.
const ordinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

// Top-3 placements get a design medal; everyone else just the rank chip.
const rankMedal = (rank: number | null): ProfileAssetName | null =>
  rank === 1
    ? 'goldMedal'
    : rank === 2
    ? 'silverMedal'
    : rank === 3
    ? 'bronzeMedal'
    : null;

const rankBadgeColor = (rank: number | null) => {
  if (rank === 1) {
    return {backgroundColor: '#FDF1C8', color: '#8A6D1D'};
  }
  if (rank === 2 || rank === 3) {
    return {backgroundColor: '#F0EDE7', color: '#4D3F37'};
  }
  return {backgroundColor: '#FAF6EB', color: '#4D3F37'};
};

// Colors are presentational only -- state strings come straight from
// `ClubChallengeRow.state` and aren't normalized here, so an unrecognized
// state just falls back to the neutral chip rather than crashing.
const stateChipColor = (state: string) => {
  const s = state.toLowerCase();
  if (s === 'accepted' || s === 'won') {
    return {backgroundColor: '#E4F4E0', color: '#1E7A3D'};
  }
  if (s === 'pending' || s === 'open') {
    return {backgroundColor: '#FDF1C8', color: '#8A6D1D'};
  }
  if (s === 'expired' || s === 'cancelled' || s === 'rejected') {
    return {backgroundColor: '#FBE4E1', color: '#CD2C39'};
  }
  return {backgroundColor: '#F0EDE7', color: '#4D3F37'};
};

type Props = {
  username?: string;
  navigation?: any;
};

// Open to any authenticated viewer (owner or visitor), not owner-only --
// same as Games.
export default function ClubsTab({username, navigation}: Props) {
  const {data, isLoading, isError} = useGetPlayerClubsApi(username);

  if (isLoading) {
    return (
      <Box style={styles.card}>
        <CCLoader loading={true} isComponentLoader />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box style={styles.card}>
        <CCText style={styles.emptyText}>Couldn't load clubs.</CCText>
      </Box>
    );
  }

  return (
    <>
      <View style={styles.columns}>
        <View style={styles.leftCol}>
          <Box style={styles.card}>
            <CCText style={styles.sectionLabel}>Clubs</CCText>
            {data.clubs.length ? (
              <View style={styles.clubGrid}>
                {data.clubs.map(c => (
                  <Pressable
                    key={c.club_id}
                    testID="player-club-row"
                    style={styles.clubCard}
                    onPress={() =>
                      navigation?.navigate('ClubProfile', {clubId: c.club_id})
                    }>
                    <View style={styles.clubAvatar}>
                      <CCText style={styles.clubAvatarText}>
                        {initials(c.name)}
                      </CCText>
                    </View>
                    <View>
                      <CCText fontWeight="semibold" style={styles.clubName}>
                        {c.name}
                      </CCText>
                      <CCText style={styles.clubRole}>{c.role}</CCText>
                    </View>
                  </Pressable>
                ))}
              </View>
            ) : (
              <CCText style={styles.emptyText}>
                Not a member of any club yet.
              </CCText>
            )}
          </Box>

          <Box style={styles.card}>
            <CCText style={styles.sectionLabel}>Club challenges</CCText>
            {data.challenges.length ? (
              data.challenges.map(ch => {
                const chipColor = stateChipColor(ch.state);
                const otherName = ch.is_mine
                  ? ch.opponent ?? 'open'
                  : ch.created_by ?? 'Someone';
                // Design phrases direction explicitly: outgoing ("You
                // challenged X") vs incoming ("X challenged you").
                const title = ch.is_mine
                  ? `You challenged ${otherName}`
                  : `${otherName} challenged you`;
                return (
                  <View key={ch.id} style={styles.challengeRow}>
                    <View style={styles.challengeAvatar}>
                      <CCText style={styles.challengeAvatarText}>
                        {initials(otherName)}
                      </CCText>
                    </View>
                    <View style={{flex: 1}}>
                      <CCText style={styles.rowTitle}>{title}</CCText>
                      <CCText style={styles.rowMeta}>{ch.time_control}</CCText>
                    </View>
                    <CCText
                      style={[
                        styles.stateChip,
                        {
                          backgroundColor: chipColor.backgroundColor,
                          color: chipColor.color,
                        },
                      ]}>
                      {ch.state}
                    </CCText>
                  </View>
                );
              })
            ) : (
              <CCText style={styles.emptyText}>No club challenges yet.</CCText>
            )}
          </Box>
        </View>

        <View style={styles.rightCol}>
          <Box style={styles.card}>
            <CCText style={styles.sectionLabel}>
              Tournaments{' '}
              {data.tournaments.length ? `(${data.tournaments.length})` : ''}
            </CCText>
            {data.tournaments.length ? (
              data.tournaments.slice(0, TOURNAMENTS_LIMIT).map((t, idx) => {
                const badge = rankBadgeColor(t.final_rank);
                const medal = rankMedal(t.final_rank);
                const meta = [
                  t.date,
                  t.points != null ? `${t.points} pts` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <View key={`${t.name}-${idx}`} style={styles.row}>
                    {medal ? (
                      <ProfileAsset
                        name={medal}
                        size={28}
                        style={styles.medal}
                      />
                    ) : null}
                    <View style={{flex: 1, minWidth: 0}}>
                      <CCText style={styles.rowTitle} numberOfLines={1}>
                        {t.name}
                      </CCText>
                      {meta ? (
                        <CCText style={styles.rowMeta}>{meta}</CCText>
                      ) : null}
                    </View>
                    <CCText style={[styles.rankText, {color: badge.color}]}>
                      {t.final_rank != null ? ordinal(t.final_rank) : '—'}
                    </CCText>
                  </View>
                );
              })
            ) : (
              <CCText style={styles.emptyText}>
                No tournaments played yet.
              </CCText>
            )}
            {data.tournaments.length > TOURNAMENTS_LIMIT ? (
              <CCText style={styles.moreText}>
                +{data.tournaments.length - TOURNAMENTS_LIMIT} more
              </CCText>
            ) : null}
          </Box>
        </View>
      </View>
    </>
  );
}
